<script>
import {onMounted, ref} from "vue";
import {useSecurityStore} from "@/stores/Security";
import axios from "axios";
import useSharedMethod from "@/use/SharedMethod";

export default {
    setup() {
        
        const {priceFormat} = useSharedMethod();
        
        const securityStore = useSecurityStore();
        
        const wire_racks = ref([]);
        const update_grille = ref(false);
        const loading = ref(false);
        const validate_loading = ref(false);
        const max_rung = ref(0);
        
        function get_grille() {
            loading.value = true;
            axios.get(`/classes/grille`).then((response) => {
                wire_racks.value = response.data.elements;
                max_rung.value = response.data.maxRung;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function f_cancel_updating() {
            update_grille.value = false;
            get_grille();
        }
        
        function f_validate() {
            validate_loading.value = true;
            axios.post(`/grilles`).then(() => {
                update_grille.value = false;
                get_grille();
            }).finally(() => {
                validate_loading.value = false;
            });
        }
        
        function setBaseSalary(id, baseSalary) {
            loading.value = true;
            axios.put(`/categories/${id}/setBaseSalary/${baseSalary}`).then(() => {
                get_grille();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        onMounted(() => get_grille());
        
        return {
            securityStore,
            wire_racks,
            update_grille,
            loading,
            validate_loading,
            max_rung,
            get_grille,
            f_cancel_updating,
            f_validate,
            setBaseSalary,
            priceFormat,
        }
    }
};
</script>

<template>
    <div>
        <w-app :title="$t('module.wire_rack')">
            <template v-slot:rightToolbar>
                <w-btn-update
                    :show="!update_grille && securityStore.authorities.UPDATE_GRILLE"
                    @press="update_grille = true"
                />
                <w-btn
                    v-if="update_grille && securityStore.authorities.UPDATE_GRILLE"
                    color="teal"
                    icon="mdi-check-all"
                    :text="$t('common.validate')"
                    :loading="validate_loading"
                    @press="f_validate"
                />
                <w-btn
                    v-if="update_grille"
                    color="red"
                    icon="mdi-close-circle"
                    :text="$t('common.cancel')"
                    @press="f_cancel_updating"
                />
                <w-btn-reload :loading="loading" @press="get_grille"/>
                <w-btn
                    color="cyan"
                    icon="mdi-alpha-r-circle-outline"
                    :text="$t('module.categories')"
                    @press="$router.push({name: 'categories'})"
                />
                <w-btn
                    color="cyan"
                    icon="mdi-alpha-c-circle-outline"
                    :text="$t('module.classes')"
                    @press="$router.push({name: 'classes'})"
                />
            </template>
            
            <template v-slot:content>
                <v-skeleton-loader
                    v-if="loading"
                    ref="skeleton"
                    :type="'table'"
                    class="mx-auto"
                ></v-skeleton-loader>
                <div style="overflow-x: auto" v-else>
                    <table class="content-table sortable">
                        <thead>
                        <tr>
                            <th class="text-center">
                                <strong>{{ $t("module.classe") }}</strong>
                            </th>
                            <th class="text-center">
                                <strong>{{ $t("module.category") }}</strong>
                            </th>
                            <th></th>
                            <th class="text-center">
                                <strong>0</strong>
                            </th>
                            <th class="text-center" v-for="n in max_rung" :key="n">
                                {{ n }}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(wire_rack, index) in wire_racks" :key="index">
                            <td
                                class="text-center"
                                style="background-color: #80cbc4"
                                v-if="wire_rack.first"
                                :rowspan="wire_rack.categoriesSize"
                            >
                                {{ wire_rack.classe }}
                            </td>
                            <td class="text-center" style="background-color: #fff176">
                                {{ wire_rack.category }}
                            </td>
                            <td class="text-center">
                                <v-icon :color="wire_rack.valid ? 'teal' : 'red'">
                                    mdi-check-underline-circle-outline
                                </v-icon>
                            </td>
                            <td
                                class="text-center"
                                v-if="update_grille"
                                style="background-color: #ffe0b2"
                            >
                                <w-edit-dialog
                                    v-model="wire_rack.baseSalary"
                                    price-format
                                    hide-chip
                                    hide-details
                                    @save=" setBaseSalary(wire_rack.categoryId, wire_rack.baseSalary) "
                                    @open="open(wire_rack.category)"
                                />
                            </td>
                            <td
                                class="text-center"
                                v-if="!update_grille"
                                style="background-color: #b3e5fc"
                            >
                                {{ priceFormat(wire_rack.baseSalary) }}
                            </td>
                            <td
                                class="text-center"
                                v-for="n in wire_rack.salaryEstimation"
                                :key="n"
                            >
                                {{ priceFormat(n) }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>

<style scoped>
.content-table tr {
    height: 10px;
}

.content-table {
    border-collapse: collapse;
    margin: 0 0;
    font-size: 0.9em;
    min-width: 400px;
    width: 100%;
    overflow: hidden;
}

.content-table thead tr {
    background-color: #009879;
    color: #ffffff;
    text-align: left;
    font-weight: bold;
}

.content-table th,
.content-table td {
    padding: 5px 5px;
}

.content-table tbody tr {
    border-bottom: 1px solid #dddddd;
    overflow: scroll;
}

.content-table table,
td {
    border: 1px solid grey;
}

.content-table tbody tr:last-of-type {
    border-bottom: 0px solid #009879;
}

.content-table tbody tr.active-row {
    font-weight: bold;
    color: #009879;
}
</style>
